import {gql} from 'graphql-request';
import {useGqlRequest} from '@/api/request';
import {useQuery} from '@tanstack/react-query';
import {useDebounce} from '@uidotdev/usehooks';
import {GeoDataItem, GeodataPointVars, RegionItemData} from '../@types';
import {GeoDataType, RegionTypes} from '../enums';

export const useQueryRegionGeoData = ({
  parentCode,
  regionType,
  includeGeometryData,
}: {
  parentCode?: string;
  regionType: RegionTypes;
  includeGeometryData?: boolean;
}) => {
  const query = includeGeometryData
    ? QUERY_GEODATA_MANY_GEOMETRY
    : QUERY_GEODATA_MANY_BASIC;

  const request = useGqlRequest({
    query,
  });

  const queryFn = ():
    | undefined
    | Promise<{
        result?: RegionItemData[];
      }> => {
    let properties: {[key: string]: any} | undefined;

    if (regionType === RegionTypes.sig) {
      properties = {sido: parentCode};
    } else if (regionType === RegionTypes.emd) {
      properties = {sgg: parentCode};
    }

    if (!regionType) {
      return;
    }

    return request({
      category: regionType,
      properties,
    });
  };

  const select = (data?: {result?: RegionItemData[]}) => data?.result;

  return useQuery({
    queryFn,
    queryKey: ['region', regionType, parentCode],
    enabled: !!regionType,
    select,
  });
};

export const useQueryRegionByPoint = (vars?: GeodataPointVars, debounceMs = 0) => {
  const request = useGqlRequest({
    query: QEURY_REGION_BY_POINT,
  });
  const debouncedVars = useDebounce(vars, debounceMs);
  const activeVars: GeodataPointVars | undefined = debounceMs > 0 ? debouncedVars : vars;

  const queryFn = ():
    | undefined
    | Promise<{
        result?: GeoDataItem;
      }> => {
    if (!activeVars) {
      return;
    }

    const requestVars = {
      ...activeVars,
      type: activeVars.type ?? GeoDataType.Emd,
    };

    return request(requestVars);
  };

  const select = (data?: {result?: GeoDataItem}) => data?.result;

  return useQuery({
    queryFn,
    queryKey: ['region', 'current', activeVars],
    enabled: !!activeVars,
    placeholderData: (prev) => prev,
    select,
  });
};

const GEODATA_BASIC = `
category
code
name
properties
`;

const GEODATA_WITH_LOCATION = `
  ${GEODATA_BASIC}
  location {
    type
    coordinates
  }
`;

const QEURY_REGION_BY_POINT = gql`
  query geoDataByPoint($lat: Float!, $lng: Float!, $type: GeoDataType!) {
    result: geoDataByPoint(lat: $lat, lng: $lng, type: $type) {
      admCode
      code
      emd
      geometry {
        coordinates
        type
      }
      location {
        coordinates
        type
      }
      name
      sgg
      sggCode
      sido
      sidoCode
      type
    }
  }
`;

const QUERY_GEODATA_MANY_BASIC = gql`
  query geodataMany($category: EnumGeoDataCategory, $properties: JSON) {
    result: geodataMany(
      filter: {category: $category, properties: $properties}
    ) {
      ${GEODATA_BASIC}
    }
  }
`;

const QUERY_GEODATA_MANY_GEOMETRY = gql`
  query geodataMany($category: EnumGeoDataCategory, $properties: JSON) {
    result: geodataMany(
      filter: {category: $category, properties: $properties}
    ) {
      ${GEODATA_WITH_LOCATION}
    }
  }
`;
